import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  UsersIcon,
  DocumentDuplicateIcon,
  ShoppingCartIcon,
  DocumentIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ChatBubbleLeftRightIcon, // Import WhatsApp icon
  IdentificationIcon, // Import icon for Contacts
  CalendarDaysIcon, // Icon for Availability
  DocumentMagnifyingGlassIcon,
  EnvelopeIcon, // Icon for Audit Log
  FolderIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import axiosInstance from '../../api/axiosConfig';
import { useSettings } from '../../contexts/SettingsContext';

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  text: string;
  isActive: boolean;
  permission?: string; // Changed from roles
  isCollapsed: boolean;
  unreadCount?: number;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon, text, isActive, permission, isCollapsed, unreadCount }) => { // Changed roles to permission
  const { can } = useAuth(); // Changed isAllowed to can

  if (permission && !can(permission)) { // Changed roles to permission and isAllowed to can
    return null;
  }

  return (
    <Link
      to={to}
      title={isCollapsed ? text : undefined}
      className={`flex items-center px-4 py-2 rounded-md transition-colors duration-200 ${isCollapsed ? 'justify-center' : ''} ${isActive
        ? 'bg-primary text-white shadow-lg'
        : 'text-gray-600 hover:bg-gray-100'
        }`}
    >
      <div className={`w-6 h-6 flex-shrink-0 ${isCollapsed ? '' : 'mr-3'}`}>{icon}</div>
      {!isCollapsed && (
        <span className="flex items-center">
          {text}
          {(unreadCount && unreadCount > 0) ? (
            <span className="ml-2 bg-red-500 text-white text-xs font-semibold px-1.5 py-0.5 rounded-full">
              {unreadCount}
            </span>
          ) : ''}
        </span>
      )}
    </Link>
  );
};

interface SidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  toggleCollapse: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, isCollapsed, toggleCollapse }) => {
  const location = useLocation();
  const [unreadWhatsAppCount, setUnreadWhatsAppCount] = useState(0);
  const { settings, loading: settingsLoading, error: settingsError } = useSettings();

  const fetchUnreadCount = async () => {
    try {
      const response = await axiosInstance.get('/whatsapp/unread-count');
      setUnreadWhatsAppCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread WhatsApp count:', error);
      // Optionally set to 0 or handle error display
      setUnreadWhatsAppCount(0);
    }
  };

  useEffect(() => {
    fetchUnreadCount(); // Initial fetch
    const intervalId = setInterval(fetchUnreadCount, 5000); // Fetch every 5 seconds

    return () => clearInterval(intervalId); // Cleanup interval on component unmount
  }, []);

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === path || location.pathname === '/';
    }
    // Make isActive more specific for parent routes
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <aside
      className={`bg-white shadow-lg fixed top-0 left-0 h-full pt-16 z-20
                transition-all duration-300 ease-in-out
                ${isCollapsed ? 'w-20' : 'w-64'}
                ${isOpen ? 'translate-x-0' : '-translate-x-full'}
                md:translate-x-0
              `}
    >
      <div className="h-full flex flex-col justify-between">
        <nav className={`space-y-2 overflow-y-auto px-2 py-4 ${isCollapsed ? 'px-2' : 'px-4'}`}>
          <NavItem
            to="/dashboard"
            icon={<HomeIcon />}
            text="Дашбоард"
            permission="dashboard.view" // Changed from roles
            isActive={isActive('/dashboard')}
            isCollapsed={isCollapsed}
          />
          {/* Use NavItem for WhatsApp Standalone link */}
          <NavItem
            to="/whatsapp-standalone" // Link destination
            icon={<ChatBubbleLeftRightIcon />} // Icon
            text="WhatsApp Чат" // Text label
            permission="whatsapp.view" // Changed from roles
            isActive={isActive('/whatsapp')} // Use existing active logic (highlights based on /whatsapp path)
            isCollapsed={isCollapsed} // Pass collapse state
            unreadCount={unreadWhatsAppCount}
          // Note: This NavItem uses react-router Link, so target="_whatsapp" behavior is removed.
          // If opening in a specific window/tab is crucial, NavItem would need modification or use a styled <a> tag.
          />
          <NavItem
            to="/email-standalone" // Link destination
            icon={<EnvelopeIcon />} // Icon
            text="Почта" // Text label
            permission="whatsapp.view" // Changed from roles
            isActive={isActive('/email')} // Use existing active logic (highlights based on /email path)
            isCollapsed={isCollapsed} // Pass collapse state
          // Note: This NavItem uses react-router Link, so target="_whatsapp" behavior is removed.
          // If opening in a specific window/tab is crucial, NavItem would need modification or use a styled <a> tag.
          />
          <NavItem
            to="/structures"
            icon={<DocumentDuplicateIcon />}
            text="Товары и услуги"
            permission="structures.view" // Changed from roles
            isActive={isActive('/structures') && !isActive('/structures/availability') && !isActive('/structures/price-list') && !isActive('/structures/new-price-list')}
            isCollapsed={isCollapsed}
          />
          {/* <NavItem
            to="/structures/price-list"
            icon={<DocumentDuplicateIcon />} // Or a more specific icon
            text="Прайс-лист (старый)"
            permission="structures.view"
            isActive={isActive('/structures/price-list')}
            isCollapsed={isCollapsed}
          /> */}
          <NavItem
            to="/structures/new-price-list"
            icon={<ClipboardDocumentListIcon />} // Using a different icon for distinction
            text="Прайс-лист"
            permission="structures.view" // Assuming same permission
            isActive={isActive('/structures/new-price-list')}
            isCollapsed={isCollapsed}
          />
          <NavItem
            to="/structures/availability"
            icon={<CalendarDaysIcon />}
            text="Свободка"
            permission="structures.view" // Changed from roles
            isActive={isActive('/structures/availability')}
            isCollapsed={isCollapsed}
          />
          <NavItem
            to="/clients"
            icon={<UsersIcon />}
            text="Клиенты"
            permission="clients.view" // Changed from roles
            isActive={isActive('/clients')}
            isCollapsed={isCollapsed}
          />
          <NavItem
            to="/files"
            icon={<FolderIcon />}
            text="Файлы"
            permission="files.view"
            isActive={isActive('/files')}
            isCollapsed={isCollapsed}
          />
          {/* Add Contacts NavItem */}
          <NavItem
            to="/contacts"
            icon={<IdentificationIcon />}
            text="Контакты"
            isActive={isActive('/contacts')}
            // Assuming similar roles to clients view/manage
            permission="contacts.view" // Changed from roles
            isCollapsed={isCollapsed}
          />
          <NavItem
            to="/quotes"
            icon={<DocumentIcon />}
            text="КП"
            isActive={isActive('/quotes')}
            permission="quotes.view" // Changed from roles
            isCollapsed={isCollapsed}
          />
          <NavItem
            to="/orders"
            icon={<ShoppingCartIcon />}
            text="Заказы"
            isActive={isActive('/orders')}
            permission="orders.view" // Changed from roles
            isCollapsed={isCollapsed}
          />
          <NavItem
            to="/contracts"
            icon={<DocumentIcon />}
            text="Договоры"
            isActive={isActive('/contracts')}
            permission="contracts.view" // Changed from roles
            isCollapsed={isCollapsed}
          />
          <NavItem
            to="/work-orders"
            icon={<ClipboardDocumentListIcon />}
            text="Наряды на работы"
            isActive={isActive('/work-orders')}
            permission="workOrders.view" // Changed from roles
            isCollapsed={isCollapsed}
          />
          <NavItem
            to="/payments"
            icon={<CurrencyDollarIcon />}
            text="Платежи"
            isActive={isActive('/payments')}
            permission="payments.view" // Changed from roles
            isCollapsed={isCollapsed}
          />
          <NavItem
            to="/expenses"
            icon={<BanknotesIcon />}
            text="Расходы"
            isActive={isActive('/expenses')}
            permission="expenses.view" // Changed from roles
            isCollapsed={isCollapsed}
          />
          <NavItem
            to="/reports"
            icon={<ChartBarIcon />}
            text="Отчеты"
            isActive={isActive('/reports')}
            permission="reports.view" // Changed from roles
            isCollapsed={isCollapsed}
          />
          <div className="pt-4 pb-2">
            <span className={`px-4 text-xs font-semibold text-gray-500 uppercase ${isCollapsed ? 'hidden' : ''}`}>Настройки</span>
            {isCollapsed && <hr className="mx-2 border-gray-200" />}
          </div>
          <NavItem
            to="/settings/general"
            icon={<Cog6ToothIcon />}
            text="Общие настройки"
            isActive={isActive('/settings/general')}
            permission="settings.general.view" // Changed from roles
            isCollapsed={isCollapsed}
          />
          <NavItem
            to="/settings/users"
            icon={<UsersIcon />}
            text="Пользователи"
            isActive={isActive('/settings/users')}
            permission="settings.user_management" // Changed from roles
            isCollapsed={isCollapsed}
          />
          <NavItem
            to="/settings/permissions"
            icon={<ShieldCheckIcon />}
            text="Права доступа"
            isActive={isActive('/settings/permissions')}
            permission="settings.permissions.view" // Changed from roles
            isCollapsed={isCollapsed}
          />
          <NavItem
            to="/settings/audit-log"
            icon={<DocumentMagnifyingGlassIcon />}
            text="Журнал аудита"
            isActive={isActive('/settings/audit-log')}
            permission="settings.audit_log.view" // Assuming a new permission for audit log
            isCollapsed={isCollapsed}
          />
        </nav>

        <div className="p-4 border-t border-gray-200">
          <button
            onClick={toggleCollapse}
            className="w-full flex items-center justify-center p-2 rounded-md text-gray-600 hover:bg-gray-100"
            title={isCollapsed ? 'Развернуть меню' : 'Свернуть меню'}
          >
            {isCollapsed ? (
              <ArrowsPointingOutIcon className="h-6 w-6" />
            ) : (
              <ArrowsPointingInIcon className="h-6 w-6" />
            )}
            <span className="sr-only">{isCollapsed ? 'Развернуть' : 'Свернуть'}</span>
          </button>
          <span className={`text-xs text-gray-500 ${isCollapsed ? 'hidden' : ''}`}>
            {`Версия №${settings?.gitInfo?.commitCount || 'N/A'} от ${settings?.gitInfo?.commitDate ? new Date(settings.gitInfo.commitDate).toLocaleString() : 'N/A'}`}
            <br />
            {`Коммит от (${settings?.gitInfo?.commitAuthor || 'N/A'})`}
          </span>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
